/**
 * Seeding crfPackage/visitMapping (the Form Assignments grid) from the matched
 * schedule. ONE copy of the rule, used by the CRF Builder page
 * (web/src/components/crf/VisitScheduleMatrix.tsx) and by the server's
 * automatic match after CRF approval (schedule/autoMatchAfterApproval.ts), so
 * the two can never seed differently.
 *
 * visits[].forms holds ONLY real CRF form names, put there by the strict
 * matcher (visitFormMatch.ts). So seeding is a plain lookup by name: exact, or
 * the same name ignoring case and punctuation. No substring and no shared-word
 * guessing.
 *
 * Pure. No React, no Firestore.
 */

export interface SeedForm { id: string; name: string; category?: string }
export interface SeedVisit {
  id: string;
  name?: unknown;
  forms?: unknown;
  assessments?: unknown;
  formsToConfirm?: unknown;
  /** How a label was matched (from matchScheduleFormsV2); wording matches are shown as such. */
  formMatches?: unknown;
  window?: unknown;
  timing?: unknown;
  day?: unknown;
}

const norm = (s: string) => s.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim();

export function seedAssignmentsFromMatchedVisits(
  visits: SeedVisit[],
  forms: SeedForm[],
): { assignments: Map<string, Set<string>>; ticks: number } {
  const assignments = new Map<string, Set<string>>();
  forms.forEach((f) => assignments.set(f.id, new Set()));
  const byName = new Map<string, string>();
  for (const f of forms) {
    if (!byName.has(f.name)) byName.set(f.name, f.id);
    const k = norm(f.name);
    if (k && !byName.has(`~${k}`)) byName.set(`~${k}`, f.id);
  }
  let ticks = 0;
  for (const v of visits ?? []) {
    for (const ref of Array.isArray(v?.forms) ? (v.forms as unknown[]) : []) {
      if (typeof ref !== 'string' || !ref.trim()) continue;
      const id = byName.get(ref.trim()) ?? byName.get(`~${norm(ref)}`);
      if (id && !assignments.get(id)!.has(v.id)) {
        assignments.get(id)!.add(v.id);
        ticks++;
      }
    }
  }
  return { assignments, ticks };
}

/**
 * The window text written into crfPackage/visitMapping for a visit.
 *
 * Only the visit's OWN window/timing text is stored. A "Day N" label is never
 * derived from `day` here: the mapping is a frozen copy and went stale when the
 * schedule was corrected. Readers (the CRF Completion Guide) overlay the live
 * timing from studyDesign/config; old documents that carry `window` still read.
 * Returns undefined when there is none, so callers omit the field.
 */
export function ownWindowText(v: { window?: unknown; timing?: unknown; day?: unknown }): string | undefined {
  for (const t of [v.window, v.timing]) {
    if (typeof t === 'string' && t.trim()) return t.trim();
  }
  return undefined;
}

export interface SeededVisitMappingBody<Stamp> {
  visits: Array<{ id: string; name: string; window?: string; forms: string[] }>;
  form_inventory: Array<{ id: string; name: string; layout: 'question-list'; category: string }>;
  editedAt: Stamp;
  editedBy: string;
}

/**
 * The crfPackage/visitMapping document a seed writes: every visit with the ids
 * of the forms ticked for it, and the form inventory. The caller adds its own
 * docVersion (the page through its versioned write, the server inside its
 * transaction).
 */
export function buildSeededVisitMapping<Stamp>(
  visits: Array<{ id: string; name?: unknown; window?: unknown; timing?: unknown; day?: unknown }>,
  forms: SeedForm[],
  assignments: Map<string, Set<string>>,
  stamp: { editedAt: Stamp; editedBy: string },
): SeededVisitMappingBody<Stamp> {
  return {
    visits: visits.map((v) => {
      const w = ownWindowText(v);
      return {
        id: v.id,
        name: v.name as string,
        ...(w !== undefined ? { window: w } : {}),
        forms: forms.filter((f) => assignments.get(f.id)?.has(v.id)).map((f) => f.id),
      };
    }),
    form_inventory: forms.map((f) => ({
      id: f.id,
      name: f.name,
      layout: 'question-list' as const,
      // A missing category is written as '' (Firestore refuses undefined).
      category: f.category ?? '',
    })),
    editedAt: stamp.editedAt,
    editedBy: stamp.editedBy,
  };
}
